<template>
  <v-dialog v-model="dialog" max-width="550" :retain-focus="false">
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        icon
        x-small
        v-bind="attrs"
        v-on="on"
        class="help-icon-btn"
        tabindex="-1"
      >
        <v-icon small>mdi-help-circle-outline</v-icon>
      </v-btn>
    </template>

    <v-card class="help-dialog-card">
      <!-- Glass-morphism gradient header -->
      <div class="help-dialog-header">
        <svg class="header-pattern" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
          <defs>
            <pattern id="help-pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
              <circle cx="20" cy="20" r="1.5" fill="rgba(255,255,255,0.15)" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#help-pattern)" />
        </svg>

        <div class="header-content">
          <v-icon left color="white" size="28">mdi-information-outline</v-icon>
          <span class="header-title">{{ title }}</span>
        </div>

        <v-btn icon absolute right top class="close-btn" @click="dialog = false">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="help-dialog-body">
        <!-- Required/Optional badge -->
        <v-chip
          small
          :class="required ? 'badge-required' : 'badge-optional'"
          class="mb-4"
        >
          <v-icon left x-small>{{ required ? 'mdi-alert-circle' : 'mdi-check-circle' }}</v-icon>
          {{ required ? 'OBAVEZNO POLJE' : 'OPCIONALNO POLJE' }}
        </v-chip>

        <!-- Description -->
        <div class="help-description">
          {{ description }}
        </div>

        <!-- Example if provided -->
        <div v-if="example" class="help-section">
          <div class="section-divider"></div>
          <div class="section-label">
            <v-icon small color="#3989C9" class="mr-1">mdi-lightbulb-on-outline</v-icon>
            Primjer:
          </div>
          <div class="section-content example-content">{{ example }}</div>
        </div>

        <!-- Format if provided -->
        <div v-if="format" class="help-section">
          <div class="section-divider"></div>
          <div class="section-label">
            <v-icon small color="#3989C9" class="mr-1">mdi-format-text</v-icon>
            Format:
          </div>
          <div class="section-content format-content">{{ format }}</div>
        </div>

        <!-- Additional notes if provided -->
        <div v-if="notes" class="help-section">
          <div class="section-divider"></div>
          <div class="section-label">
            <v-icon small color="#3989C9" class="mr-1">mdi-note-text-outline</v-icon>
            Napomena:
          </div>
          <div class="section-content notes-content">{{ notes }}</div>
        </div>
      </v-card-text>

      <v-card-actions class="help-dialog-actions">
        <v-spacer></v-spacer>
        <v-btn class="understand-btn" @click="dialog = false">
          <v-icon left small>mdi-check</v-icon>
          Razumijem
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'FieldHelpDialog',
  props: {
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    },
    required: {
      type: Boolean,
      default: true
    },
    example: {
      type: String,
      default: null
    },
    format: {
      type: String,
      default: null
    },
    notes: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      dialog: false
    }
  }
}
</script>

<style scoped>
/* Help icon button */
.help-icon-btn {
  color: rgba(57, 137, 201, 0.6) !important;
  margin-left: 4px;
  align-self: flex-start !important;
  margin-top: 8px !important;
  flex-shrink: 0 !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.help-icon-btn:hover {
  color: rgba(57, 137, 201, 1) !important;
  background: rgba(57, 137, 201, 0.08) !important;
  transform: scale(1.15);
}

/* Glass-morphism card */
.help-dialog-card {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 16px !important;
  border: 1px solid rgba(57, 137, 201, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
  overflow: hidden !important;
}

/* Gradient header with pattern */
.help-dialog-header {
  position: relative;
  background: linear-gradient(
    135deg,
    rgba(0, 179, 190, 0.5) 0%,
    rgba(57, 137, 201, 0.45) 50%,
    rgba(97, 84, 164, 0.5) 100%
  ) !important;
  padding: 24px 60px 24px 24px;
  overflow: hidden;
}

.header-pattern {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.4;
}

.header-content {
  position: relative;
  display: flex;
  align-items: center;
  z-index: 1;
}

.header-title {
  color: white;
  font-weight: 600;
  font-size: 18px;
  letter-spacing: 0.3px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.close-btn {
  z-index: 2;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  transform: scale(1.1) rotate(90deg);
}

/* Body content */
.help-dialog-body {
  padding: 24px !important;
}

/* Badge styles */
.badge-required {
  background: rgba(244, 67, 54, 0.12) !important;
  border: 1px solid rgba(244, 67, 54, 0.3) !important;
  color: #C62828 !important;
  font-weight: 600 !important;
  font-size: 11px !important;
  letter-spacing: 0.5px !important;
  height: 26px !important;
}

.badge-optional {
  background: rgba(76, 175, 80, 0.12) !important;
  border: 1px solid rgba(76, 175, 80, 0.3) !important;
  color: #2E7D32 !important;
  font-weight: 600 !important;
  font-size: 11px !important;
  letter-spacing: 0.5px !important;
  height: 26px !important;
}

/* Description */
.help-description {
  color: rgba(0, 0, 0, 0.8);
  font-size: 15px;
  line-height: 1.6;
  margin-bottom: 8px;
}

/* Section styles */
.help-section {
  margin-top: 20px;
}

.section-divider {
  height: 1px;
  background: linear-gradient(
    90deg,
    rgba(57, 137, 201, 0.2) 0%,
    rgba(57, 137, 201, 0.05) 100%
  );
  margin-bottom: 12px;
}

.section-label {
  display: flex;
  align-items: center;
  color: #3989C9;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.3px;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.section-content {
  color: rgba(0, 0, 0, 0.75);
  font-size: 14px;
  line-height: 1.6;
  padding: 12px 16px;
  border-radius: 8px;
  background: rgba(57, 137, 201, 0.04);
  border-left: 3px solid rgba(57, 137, 201, 0.3);
}

.example-content {
  font-style: italic;
  color: rgba(0, 0, 0, 0.7);
  font-family: 'Courier New', monospace;
}

.format-content {
  font-family: 'Courier New', monospace;
  font-weight: 500;
}

.notes-content {
  white-space: pre-line;
  line-height: 1.7;
}

/* Actions */
.help-dialog-actions {
  padding: 16px 24px 24px !important;
}

/* Understand button - soft shadow style */
.understand-btn {
  background: rgba(255, 255, 255, 0.8) !important;
  color: #3989C9 !important;
  border-radius: 12px !important;
  border: 1px solid rgba(57, 137, 201, 0.15) !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.15) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
  padding: 10px 24px !important;
  height: 40px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.understand-btn:hover {
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.25) !important;
  border-color: rgba(57, 137, 201, 0.3) !important;
  transform: translateY(-1px);
  background: rgba(255, 255, 255, 0.95) !important;
}

.understand-btn .v-icon {
  color: #3989C9 !important;
}
</style>
